import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Input, Loader } from '@dreamtree-org/twreact-ui';
import { useAuth } from '@rbac/useCan';
import { startTotp, confirmTotp, refreshMe, errorOf } from '@/lib/tenantAuth';
import AuthShell from './AuthShell';

/**
 * A14 (two-step part) — enrol an authenticator app. Standalone (no app chrome): a company
 * administrator lands here before anything else works, and the regular layout would call
 * APIs that are closed to them until they finish (STRONG_FACTOR_REQUIRED).
 */
export default function AccountSecurity() {
  const nav = useNavigate();
  const auth = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const enrolled = auth && auth.hasTotp;

  useEffect(() => {
    if (enrolled) return;
    startTotp().then(setSetup).catch((ex) => setErr(errorOf(ex).message));
  }, [enrolled]);

  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      setCodes((await confirmTotp(code)).recoveryCodes);
    } catch (ex) {
      setErr(errorOf(ex).message);
    } finally {
      setBusy(false);
      setCode('');
    }
  };

  const done = async () => {
    await refreshMe();
    nav('/', { replace: true });
  };

  return (
    <AuthShell title={codes ? 'Save your recovery codes' : 'Two-step verification'}>
      <div className="space-y-3">
        {codes ? (
          <>
            <Alert variant="warning" title="Shown only once">Each code signs you in once if you lose your phone. Keep them in your password manager.</Alert>
            <ul className="grid grid-cols-2 gap-1 font-mono text-sm bg-secondary-100 dark:bg-secondary-900 p-3 rounded" aria-label="Recovery codes">
              {codes.map((c) => <li key={c}>{c}</li>)}
            </ul>
            <Button className="w-full" onClick={done}>I&apos;ve saved them — continue</Button>
          </>
        ) : (
          <>
            {enrolled && <Alert variant="success">Two-step verification is on for your account.</Alert>}
            {!enrolled && (
              <p className="text-sm text-secondary-600">
                {auth && auth.needsStrongFactor
                  ? 'Company administrators must use an authenticator app. Scan the code, then enter the 6 digits it shows.'
                  : 'Add an authenticator app for stronger sign-in.'}
              </p>
            )}
            {err && <Alert variant="error">{err}</Alert>}
            {!enrolled && !setup && !err && <Loader />}
            {setup && (
              <>
                <img src={setup.qrDataUrl} alt="QR code for your authenticator app" className="mx-auto w-48 h-48 bg-white p-2 rounded" />
                <details className="text-xs text-secondary-600">
                  <summary className="cursor-pointer">Can&apos;t scan? Enter this key instead</summary>
                  <code className="block mt-1 break-all select-all">{setup.secretBase32}</code>
                </details>
                <form className="space-y-3" onSubmit={confirm}>
                  <Input label="Code from the app" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" inputMode="numeric" required />
                  <Button type="submit" className="w-full" disabled={busy || !code.trim()}>{busy ? 'Checking…' : 'Turn on two-step verification'}</Button>
                </form>
              </>
            )}
            {enrolled && <Button className="w-full" onClick={() => nav('/')}>Back to the app</Button>}
          </>
        )}
      </div>
    </AuthShell>
  );
}
